import React, { useState, useMemo } from 'react';
import { Search, ChevronLeft, ChevronRight, Calendar, User, X, AlertCircle, Sun, Moon, Coffee, Clock, ShieldAlert, Key, Utensils } from 'lucide-react';
import { useSharedStore } from '../../hooks/useSharedStore';
import { users, effectiveBranchesOf, shifts, weeklySchedules, updateWeeklySchedule } from '../../data/store';
import { FilterPopover } from '../../components/FilterPopover';
import { motion, AnimatePresence } from 'framer-motion';

export const StaffScheduleTab = ({ org, homeBranchId }) => {
  useSharedStore();
  const accent = org?.theme?.primary || 'var(--theme-primary)';

  const [searchTerm, setSearchTerm] = useState('');
  const [currentWeek, setCurrentWeek] = useState(() => {
    const d = new Date();
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  });
  const [filters, setFilters] = useState({ branches: [] });

  const [activeCell, setActiveCell] = useState(null); // { userId, dateStr, staffName }

  const branches = effectiveBranchesOf(org.id);
  const myShifts = shifts.filter(s => s.orgId === org.id && s.status !== 'Inactive');

  const staffMembers = users.filter(u => {
    if (u.orgId !== org.id || u.role === 'super-admin') return false;
    if (homeBranchId && u.branchId !== homeBranchId) return false;
    if (!homeBranchId && filters.branches?.length > 0 && !filters.branches.includes(u.branchId)) return false;
    return true;
  });

  const filteredStaff = staffMembers.filter(s => {
    if (searchTerm && !s.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }).map((_, i) => {
      const d = new Date(currentWeek);
      d.setDate(currentWeek.getDate() + i);
      return d;
    });
  }, [currentWeek]);

  const weekStartDateStr = weekDays[0].toISOString().split('T')[0];

  const nextWeek = () => {
    const next = new Date(currentWeek);
    next.setDate(next.getDate() + 7);
    setCurrentWeek(next);
  };

  const prevWeek = () => {
    const prev = new Date(currentWeek);
    prev.setDate(prev.getDate() - 7);
    setCurrentWeek(prev);
  };

  const handleSetOverride = (userId, dateStr, overrideValue) => {
    const patch = { [dateStr]: overrideValue };
    updateWeeklySchedule(org.id, userId, weekStartDateStr, patch);
    setActiveCell(null);
  };

  const parseHours = (timeStr) => {
    if (!timeStr) return 0;
    const [h, m] = timeStr.split(':').map(Number);
    return h + (m / 60);
  };

  const getShiftDetails = (userId, dateStr) => {
    // 1. Check exceptions/overrides in weeklySchedules first
    const scheduleRecord = weeklySchedules.find(s => s.orgId === org.id && s.userId === userId && s.weekStartDate === weekStartDateStr);
    if (scheduleRecord && scheduleRecord.schedule && scheduleRecord.schedule[dateStr] !== undefined) {
      const assigned = scheduleRecord.schedule[dateStr];
      if (assigned === 'leave') return { type: 'leave', label: 'Leave' };
      if (assigned === 'permission') return { type: 'permission', label: 'Permission' };
      if (assigned === 'clear') return null; // Explicitly cleared for the day
      
      const shift = myShifts.find(s => s.id === assigned);
      if (shift) return { type: 'shift', shift, isOverride: true };
    }
    
    // 2. Check Auto-Scheduling (defaultShiftId)
    const user = users.find(u => u.id === userId);
    if (!user) return null;
    
    // 3. Check Weekly Off (assume Sunday = 0)
    const weeklyOff = user.weeklyOff || [0];
    const dateObj = new Date(dateStr);
    if (weeklyOff.includes(dateObj.getDay())) {
      return { type: 'off', label: 'Weekly Off' };
    }
    
    if (user.defaultShiftId) {
      const shift = myShifts.find(s => s.id === user.defaultShiftId);
      if (shift) return { type: 'shift', shift, isOverride: false };
    }
    
    return null;
  };

  const calculateTotalHours = (userId) => {
    let total = 0;
    weekDays.forEach(date => {
      const dateStr = date.toISOString().split('T')[0];
      const details = getShiftDetails(userId, dateStr);
      if (details && details.type === 'shift' && details.shift) {
        const s = details.shift;
        const start = parseHours(s.startTime);
        const end = parseHours(s.endTime);
        
        let diff = end - start;
        if (s.isOvernight) {
          diff = (24 - start) + end;
        }

        if (s.breaks && s.breaks.length > 0) {
          s.breaks.forEach(b => {
            const bStart = parseHours(b.startTime);
            const bEnd = parseHours(b.endTime);
            let bDiff = bEnd - bStart;
            if (bEnd <= bStart) {
              bDiff = (24 - bStart) + bEnd;
            }
            diff -= bDiff;
          });
        }

        total += diff;
      }
    });
    return total;
  };

  const formatTimeStr = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':');
    let hr = parseInt(h, 10);
    const ampm = hr >= 12 ? 'PM' : 'AM';
    hr = hr % 12 || 12;
    return `${hr}:${m} ${ampm}`;
  };

  const renderCellContent = (userId, dateStr) => {
    const details = getShiftDetails(userId, dateStr);
    
    if (!details) {
      return (
        <div className="w-full h-full min-h-[48px] flex items-center justify-center opacity-0 group-hover/cell:opacity-100 transition-opacity">
          <span className="text-[10px] font-bold text-slate-300 uppercase tracking-widest">+ Override</span>
        </div>
      );
    }

    if (details.type === 'leave') {
      return (
        <div className="w-full h-full min-h-[48px] flex flex-col items-center justify-center rounded-lg border border-red-200 shadow-sm relative overflow-hidden bg-red-50"
             style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(239, 68, 68, 0.05) 10px, rgba(239, 68, 68, 0.05) 20px)' }}>
          <Coffee size={14} className="text-red-400 mb-0.5" />
          <span className="text-[10px] font-bold text-red-600 uppercase tracking-widest z-10">Leave</span>
        </div>
      );
    }
    
    if (details.type === 'permission') {
      return (
        <div className="w-full h-full min-h-[48px] flex flex-col items-center justify-center rounded-lg bg-orange-50 border border-orange-200 shadow-sm">
          <ShieldAlert size={14} className="text-orange-500 mb-0.5" />
          <span className="text-[10px] font-bold text-orange-600 uppercase tracking-widest text-center">Permission<br/><span className="text-[10px] font-bold opacity-80">Half Day</span></span>
        </div>
      );
    }

    if (details.type === 'off') {
      return (
        <div className="w-full h-full min-h-[48px] flex flex-col items-center justify-center rounded-lg border border-slate-200"
             style={{ background: 'repeating-linear-gradient(-45deg, #f8fafc, #f8fafc 8px, #f1f5f9 8px, #f1f5f9 16px)' }}>
          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest bg-white/90 px-2 py-0.5 rounded shadow-sm border border-slate-200">Off</span>
        </div>
      );
    }

    const s = details.shift;
    if (s.isOvernight) {
      return (
        <div className={`w-full h-full min-h-[48px] flex flex-col items-center justify-center rounded-lg bg-gradient-to-b from-indigo-900 to-slate-900 border ${details.isOverride ? 'border-amber-400 shadow-[0_0_0_1px_#fbbf24]' : 'border-indigo-950'} shadow-sm relative`}>
          <Moon size={14} className="text-indigo-300 mb-0.5" />
          <span className="text-[10px] font-bold text-white truncate max-w-[90%]">{s.code || s.name}</span>
          <span className="text-[10px] font-semibold text-indigo-300/80 mt-0.5">{formatTimeStr(s.startTime)}</span>
          {s.breaks && s.breaks.length > 0 && (
            <div className="absolute top-1 right-1 flex gap-0.5 opacity-80">
              {s.breaks.map(b => b.type === 'lunch' ? <Utensils key={b.id} size={9} className="text-indigo-300" /> : <Coffee key={b.id} size={9} className="text-indigo-300" />)}
            </div>
          )}
          {details.isOverride && (
            <div className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-amber-500 rounded-full border-2 border-white flex items-center justify-center" title="Override">
              <Key size={8} className="text-white" />
            </div>
          )}
        </div>
      );
    }

    // Regular Day Shift
    return (
      <div className={`w-full h-full min-h-[48px] flex flex-col items-center justify-center rounded-lg bg-gradient-to-b from-sky-50 to-white border ${details.isOverride ? 'border-amber-400 shadow-[0_0_0_1px_#fbbf24]' : 'border-sky-200'} shadow-sm relative`}>
        <Sun size={14} className="text-amber-500 mb-0.5" />
        <span className="text-[10px] font-bold text-slate-800 truncate max-w-[90%]">{s.code || s.name}</span>
        <span className="text-[10px] font-bold text-slate-500 mt-0.5">{formatTimeStr(s.startTime)} - {formatTimeStr(s.endTime)}</span>
        {s.breaks && s.breaks.length > 0 && (
          <div className="absolute top-1 right-1 flex gap-0.5 opacity-80">
            {s.breaks.map(b => b.type === 'lunch' ? <Utensils key={b.id} size={9} className="text-amber-500" /> : <Coffee key={b.id} size={9} className="text-amber-500" />)}
          </div>
        )}
        {details.isOverride && (
          <div className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-amber-500 rounded-full border-2 border-white flex items-center justify-center" title="Override">
            <Key size={8} className="text-white" />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full gap-4 relative">
      {/* Header & Controls (Single Line) */}
      <section className="bg-white border border-[var(--theme-tertiary)] rounded-md px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-sm shrink-0">
        
        {/* Search & Filters (Left) */}
        <div className="flex items-center gap-3 w-full sm:w-auto flex-1">
          <div className="relative flex-1 max-w-[300px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              placeholder="Search providers..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full bg-[var(--theme-secondary)] border border-[var(--theme-tertiary)] focus:bg-white rounded-md pl-9 pr-3 py-2 text-xs font-bold text-[#0d0d0d] outline-none transition-colors"
            />
          </div>

          {!homeBranchId && branches.length > 0 && (
            <FilterPopover
              accent={accent}
              align="left"
              categories={[{ id: 'branches', label: 'Branches', options: branches.map(b => ({ id: b.id, label: `${b.name}${b.isMain ? ' (Main)' : ''}` })) }]}
              initialFilters={filters}
              onApply={(newFilters) => setFilters(newFilters)}
            />
          )}
        </div>

        {/* Week Nav (Right) */}
        <div className="flex items-center justify-center gap-3 shrink-0 w-full sm:w-auto">
          <button onClick={prevWeek} className="p-1.5 rounded bg-white border border-slate-200 shadow-sm text-slate-600 hover:text-[var(--theme-primary)] hover:border-[var(--theme-primary)] transition-all">
            <ChevronLeft size={16} strokeWidth={3} />
          </button>
          <div className="text-[12px] font-bold text-slate-800 uppercase tracking-widest min-w-[140px] text-center flex-1 sm:flex-none">
            {weekDays[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {weekDays[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </div>
          <button onClick={nextWeek} className="p-1.5 rounded bg-white border border-slate-200 shadow-sm text-slate-600 hover:text-[var(--theme-primary)] hover:border-[var(--theme-primary)] transition-all">
            <ChevronRight size={16} strokeWidth={3} />
          </button>
        </div>
      </section>

      {/* Enterprise Grid */}
      <section className="bg-white border border-[var(--theme-tertiary)] rounded-md shadow-sm flex-1 flex flex-col min-h-0 overflow-hidden relative">
        <div className="overflow-auto custom-scrollbar flex-1 relative">
          <table className="w-full text-left border-collapse min-w-[850px]">
            <thead className="bg-slate-50 sticky top-0 z-20 shadow-[0_1px_0_0_var(--theme-tertiary)]">
              <tr>
                <th className="px-3 py-3 border-r border-slate-200 bg-slate-50 w-[220px] sticky left-0 z-30 shadow-[1px_0_0_0_var(--theme-tertiary)]">
                  <div className="text-[10px] font-extrabold text-slate-500 uppercase tracking-widest">Team Member</div>
                </th>
                {weekDays.map((date, idx) => {
                  const isToday = date.toISOString().split('T')[0] === new Date().toISOString().split('T')[0];
                  return (
                    <th key={idx} className={`px-2 py-3 text-center border-r border-slate-200 min-w-[100px] w-[calc(100%/7)] relative ${isToday ? 'bg-[var(--theme-secondary)] shadow-[inset_0_3px_0_0_var(--theme-primary)]' : ''}`}>
                      <div className={`text-[10px] font-extrabold uppercase tracking-widest ${isToday ? 'text-[var(--theme-primary)]' : 'text-slate-400'}`}>
                        {date.toLocaleDateString('en-US', { weekday: 'short' })}
                      </div>
                      <div className="flex justify-center mt-1">
                        <div className="text-[13px] font-bold w-6 h-6 flex items-center justify-center text-slate-800">
                          {date.getDate()}
                        </div>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredStaff.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-16 text-center">
                    <User size={32} className="mx-auto text-slate-300 mb-3" />
                    <p className="text-sm font-medium text-slate-500">No service providers found.</p>
                  </td>
                </tr>
              ) : (
                filteredStaff.map(staff => {
                  const totalHrs = calculateTotalHours(staff.id);
            const isOvertime = totalHrs > 48;
                  
                  return (
                    <tr key={staff.id} className="hover:bg-slate-50/30 transition-colors group">
                      <td className="px-3 py-3 border-r border-slate-100 bg-white group-hover:bg-slate-50 sticky left-0 z-10 w-[220px] shadow-[1px_0_0_0_var(--theme-tertiary)]">
                        <div className="flex flex-col justify-center w-48 shrink-0 py-2 min-w-0">
                          <div className="min-w-0">
                            <div className="text-[13px] font-bold text-slate-800 truncate">{staff.name}</div>
                            <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest truncate">{staff.role.replace('-', ' ')}</div>
                          </div>
                        </div>
                      </td>
                      {weekDays.map((date, idx) => {
                        const dateStr = date.toISOString().split('T')[0];
                        const isToday = dateStr === new Date().toISOString().split('T')[0];
                        return (
                          <td 
                            key={idx} 
                            className={`p-1.5 border-r border-slate-100 cursor-pointer relative group/cell transition-colors hover:bg-slate-50 ${isToday ? 'bg-[var(--theme-primary)]/5' : ''}`}
                            onClick={() => setActiveCell({ userId: staff.id, dateStr, staffName: staff.name })}
                          >
                            <div className="w-full h-full border border-dashed border-transparent hover:border-slate-300 rounded-lg p-0.5 transition-all">
                              {renderCellContent(staff.id, dateStr)}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Dynamic Exception Modal */}
      <AnimatePresence>
        {activeCell && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              exit={{ opacity: 0 }} 
              className="absolute inset-0 bg-slate-900/40 backdrop-blur-md"
              onClick={() => setActiveCell(null)}
            />
            
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 15 }} 
              animate={{ opacity: 1, scale: 1, y: 0 }} 
              exit={{ opacity: 0, scale: 0.95, y: 15 }} 
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-white rounded-[24px] shadow-2xl w-full max-w-md relative overflow-hidden z-10 flex flex-col border border-slate-200"
            >
              <div className="px-6 py-5 bg-gradient-to-b from-slate-50 to-white border-b border-slate-100 flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-bold text-[#0d0d0d] tracking-tight">Manage Day: {activeCell.staffName}</h3>
                  <div className="flex items-center gap-1.5 text-[11px] font-bold text-[var(--theme-primary)] uppercase tracking-widest mt-1">
                    <Calendar size={12} />
                    {new Date(activeCell.dateStr).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                  </div>
                </div>
                <button onClick={() => setActiveCell(null)} className="w-8 h-8 flex items-center justify-center bg-slate-100 hover:bg-slate-200 text-slate-500 rounded-full transition-colors">
                  <X size={16} strokeWidth={3} />
                </button>
              </div>
              
              <div className="p-3 max-h-[60vh] overflow-auto custom-scrollbar flex flex-col gap-1.5 bg-slate-50/50">

                
                {myShifts.length > 0 && (
                  <>
                    <div className="px-3 pt-4 pb-1 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Temporary Shift Swap</div>
                    {myShifts.map(shift => (
                      <button
                        key={shift.id}
                        onClick={() => handleSetOverride(activeCell.userId, activeCell.dateStr, shift.id)}
                        className={`w-full text-left px-4 py-3.5 rounded-xl hover:shadow-md transition-all flex items-center justify-between group border border-slate-200 ${shift.isOvernight ? 'bg-slate-900 hover:bg-black border-slate-800' : 'bg-white hover:border-[var(--theme-primary)]'}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${shift.isOvernight ? 'bg-slate-800 text-indigo-400' : 'bg-sky-50 text-amber-500'}`}>
                            {shift.isOvernight ? <Moon size={18} /> : <Sun size={18} />}
                          </div>
                          <div>
                            <span className={`block text-sm font-bold transition-colors ${shift.isOvernight ? 'text-white' : 'text-slate-800 group-hover:text-[var(--theme-primary)]'}`}>{shift.name}</span>
                            <span className={`block text-[11px] font-bold ${shift.isOvernight ? 'text-slate-400' : 'text-slate-500'}`}>
                              {formatTimeStr(shift.startTime)} &mdash; {formatTimeStr(shift.endTime)}
                            </span>
                          </div>
                        </div>
                      </button>
                    ))}
                  </>
                )}

                <div className="h-px bg-slate-200 my-2 mx-2"></div>

                <button
                  onClick={() => handleSetOverride(activeCell.userId, activeCell.dateStr, 'clear')}
                  className="w-full text-center px-4 py-3 mt-1 rounded-xl text-slate-500 hover:bg-slate-200 hover:text-slate-700 text-xs font-bold transition-colors"
                >
                  Reset to Auto-Schedule
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
